import React, { useEffect, useState } from "react";
import { X, UserRoundCheck, MapPin, Loader2, AlertTriangle, CalendarOff, Clock, CheckCircle2 } from "lucide-react";
import { getCandidates, reassignIntervention } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { overlapText } from "../../../hooks/useHolidays";
import { log } from "../../../lib/log";

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

const candidateTone = (c) => {
  if (c.is_current) return "border-neutral-200 bg-neutral-50";
  if (c.recommended) return "border-emerald-300 bg-emerald-50/50";
  if (c.available) return "border-amber-200 bg-amber-50/40";
  return "border-red-200 bg-red-50/40";
};
const distanceCls = (c) => {
  if (c.distance_km == null) return "text-neutral-400";
  return c.within_radius ? "text-emerald-700" : "text-red-600 font-semibold";
};

const Candidate = ({ c, onPick, busy }) => {
  const tone = candidateTone(c);
  return (
    <div className={`border rounded-lg p-3 flex flex-wrap items-center gap-3 ${tone}`} data-testid={`candidate-${c.login}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-neutral-900 flex items-center gap-2">
          {c.nom}
          {c.is_current && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-neutral-200 text-neutral-600">actuel</span>}
          {c.recommended && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-600 text-white">recommandé</span>}
        </p>
        <p className="text-xs text-neutral-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          <span className={`flex items-center gap-1 ${distanceCls(c)}`}>
            <MapPin size={12} /> {c.distance_km == null ? "domicile non localisé" : `${c.distance_km} km (rayon ${c.radius_km} km)`}
          </span>
          <span className="flex items-center gap-1"><Clock size={12} /> {c.week_hours} h{c.weekly_hours ? ` / ${c.weekly_hours} h` : ""} cette semaine</span>
          {c.absence && <span className="flex items-center gap-1 text-red-600 font-semibold"><CalendarOff size={12} /> absent du {c.absence.date_debut} au {c.absence.date_fin}</span>}
          {c.overlap && <span className="flex items-center gap-1 text-red-600 font-semibold"><AlertTriangle size={12} /> déjà chez {c.overlap.client_nom} à {String(c.overlap.date).slice(11, 16)}</span>}
        </p>
      </div>
      {!c.is_current && (
        <button onClick={() => onPick(c)} disabled={busy} data-testid={`candidate-pick-${c.login}`}
          className={`text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50 ${c.recommended ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border border-neutral-300 text-neutral-700 hover:border-brand"}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : "Affecter"}
        </button>
      )}
    </div>
  );
};

/** Modale « Trouver un remplaçant » : easers disponibles ≤ rayon, triés par distance ; validation si hors rayon / indisponible. */
const ReplaceModal = ({ intervention, onClose, onDone }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [replaceLogin, setReplaceLogin] = useState("");
  const [confirm, setConfirm] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    getCandidates(intervention.id).then((r) => {
      setData(r.data);
      const cur = r.data.intervention.collaborateurs || [];
      if (cur.length === 1) setReplaceLogin(cur[0].login || cur[0].nom);
    }).catch((e) => { log.error("candidats:", e); toast({ title: "Candidats indisponibles", variant: "destructive" }); });
  }, [intervention.id, toast]);

  const pick = async (c, force = false) => {
    setBusy(c.login);
    try {
      const r = await reassignIntervention(intervention.id, { login: c.login, replace_login: replaceLogin, force });
      toast({ title: `Intervention affectée à ${c.nom} ✓`, description: r.data.dolibarr_error ? "Dolibarr non synchronisé (nouvel essai auto)" : "Agenda Dolibarr mis à jour · easer prévenu par email" });
      const ov = overlapText(r.data.warnings);
      if (ov) toast({ title: "⚠️ Chevauchement", description: ov, variant: "destructive" });
      setConfirm(null);
      onDone();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.needs_confirmation) setConfirm({ c, blockers: d.blockers });
      else toast({ title: "Affectation impossible", description: typeof d === "string" ? d : "Erreur", variant: "destructive" });
    } finally { setBusy(null); }
  };

  const cur = data?.intervention.collaborateurs || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="replace-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading font-bold text-lg text-neutral-900 flex items-center gap-2"><UserRoundCheck size={18} className="text-brand" /> Trouver un remplaçant</h3>
            <p className="text-sm text-neutral-500 first-letter:uppercase">{intervention.client_nom} · {fmt(intervention.date)}</p>
            {data && !data.intervention.site_known && <p className="text-xs text-amber-600 mt-1">Site non localisé : les distances ne peuvent pas être calculées.</p>}
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="replace-close"><X size={18} /></button>
        </div>

        {cur.length > 1 && (
          <label className="text-sm text-neutral-700 flex items-center gap-2 mb-3">Remplacer
            <select value={replaceLogin} onChange={(e) => setReplaceLogin(e.target.value)} data-testid="replace-who" className="border border-neutral-300 rounded-md px-2 py-1 text-sm">
              <option value="">toute l'équipe</option>
              {cur.map((c) => <option key={c.login || c.nom} value={c.login || c.nom}>{c.nom}</option>)}
            </select>
          </label>
        )}

        {confirm && (
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 mb-3" data-testid="replace-confirm">
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={15} /> {confirm.c.nom} n'est pas dans les critères :</p>
            <ul className="text-sm text-amber-800 list-disc ml-5 mt-1">{confirm.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
            <div className="flex gap-2 mt-2">
              <button onClick={() => pick(confirm.c, true)} disabled={!!busy} data-testid="replace-force" className="text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-md">Valider quand même</button>
              <button onClick={() => setConfirm(null)} data-testid="replace-cancel-confirm" className="text-xs font-semibold border border-neutral-300 px-3 py-1.5 rounded-md">Choisir un autre easer</button>
            </div>
          </div>
        )}

        {!data ? <p className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Recherche des easers disponibles…</p> : (
          <div className="space-y-2" data-testid="candidate-list">
            {data.candidates.map((c) => <Candidate key={c.login || c.nom} c={c} onPick={(x) => pick(x, false)} busy={busy === c.login} />)}
          </div>
        )}
        <p className="text-[11px] text-neutral-400 mt-3 flex items-center gap-1"><CheckCircle2 size={11} /> Recommandé = disponible (pas d'absence ni de chevauchement) et domicile à moins de {data?.default_radius_km || 40} km du site (rayon modifiable par easer dans « Utilisateurs »).</p>
      </div>
    </div>
  );
};

export default ReplaceModal;
